/**
 * SectorTile.jsx (PR_RUN_038)
 * ============================
 * Generic sector-module placeholder tile. Renders for sectors that are
 * not yet fully shipped (status: "in_development" | "planned" | "beta").
 *
 * The Wind Energy sector has its own richer component (WindTechTile.jsx)
 * with live data + glossary + seed CTA — that tile takes precedence and
 * is rendered separately in Dashboard.js.
 *
 * Visual chrome mirrors WindTechTile so the grid stays cohesive.
 *
 * Props:
 *   sector: {
 *     slug, display_name, status, status_label, accent_color,
 *     regulatory_anchor, target_release, icon, description
 *   }
 *
 * Authored: 2026-05-23 (E1, PR_RUN_038)
 */
import {
  FlaskConical, HeartPulse, Plane, Building2, Cpu, Factory,
  Lock, Calendar, ArrowRight,
} from "lucide-react";

// Backend returns icon NAMES; we resolve to the actual lucide component.
const ICON_MAP = {
  FlaskConical, HeartPulse, Plane, Building2, Cpu, Factory,
};

const STATUS_STYLES = {
  live: {
    pillBg: "bg-emerald-500/10",
    pillText: "text-emerald-400",
    pillBorder: "border-emerald-500/20",
  },
  beta: {
    pillBg: "bg-sky-500/10",
    pillText: "text-sky-400",
    pillBorder: "border-sky-500/20",
  },
  in_development: {
    pillBg: "bg-amber-500/10",
    pillText: "text-amber-400",
    pillBorder: "border-amber-500/20",
  },
  planned: {
    pillBg: "bg-slate-500/10",
    pillText: "text-slate-400",
    pillBorder: "border-slate-500/20",
  },
};

const SectorTile = ({ sector }) => {
  const Icon = ICON_MAP[sector.icon] || Building2;
  const status = STATUS_STYLES[sector.status] || STATUS_STYLES.planned;
  const isLocked = sector.status !== "live" && sector.status !== "beta";

  return (
    <div
      className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-5 transition-all"
      style={{
        boxShadow: isLocked ? "none" : `0 0 0 1px ${sector.accent_color}25`,
      }}
      data-testid={`sector-tile-${sector.slug}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: `${sector.accent_color}14` }}
          >
            <Icon className="w-4 h-4" style={{ color: sector.accent_color }} />
          </div>
          <div>
            <p className="text-white text-sm font-medium">{sector.display_name}</p>
            <p className="text-[#8B95A5] text-[10px] uppercase tracking-wide">{sector.slug}</p>
          </div>
        </div>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full border ${status.pillBg} ${status.pillText} ${status.pillBorder} flex items-center gap-1`}
          data-testid={`sector-tile-${sector.slug}-status`}
        >
          {isLocked && <Lock className="w-2.5 h-2.5" />}
          {sector.status_label}
        </span>
      </div>

      {/* Description */}
      <p className="text-[#8B95A5] text-xs mb-3 leading-relaxed">
        {sector.description}
      </p>

      {/* Meta */}
      <div className="space-y-1.5 text-[11px] text-[#8B95A5] mb-3">
        <div className="flex justify-between gap-2">
          <span>Anchor</span>
          <span className="text-slate-300 text-right truncate" title={sector.regulatory_anchor}>
            {sector.regulatory_anchor}
          </span>
        </div>
        {sector.target_release && (
          <div className="flex justify-between gap-2">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Target
            </span>
            <span className="text-slate-300">{sector.target_release}</span>
          </div>
        )}
      </div>

      {/* Footer CTA — passive for locked sectors */}
      <button
        type="button"
        disabled={isLocked}
        className={`w-full px-3 py-2 rounded-md text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
          isLocked
            ? "text-[#8B95A5] border border-[rgba(255,255,255,0.05)] cursor-not-allowed opacity-60"
            : "text-white"
        }`}
        style={!isLocked ? { background: sector.accent_color } : undefined}
        data-testid={`sector-tile-${sector.slug}-cta`}
        aria-disabled={isLocked}
      >
        {isLocked ? (
          <>Notify me on launch</>
        ) : (
          <>
            View module <ArrowRight className="w-3 h-3" />
          </>
        )}
      </button>
    </div>
  );
};

export default SectorTile;
